import { lazy, Suspense, type ReactNode } from "react";
import { createBrowserRouter, Navigate } from "react-router";
import LoginPage from "./login";
import Home from "./home";
import NotFound from "./not-found";
import { Loading } from "@/components/loading";
import { ProtectedRoute } from "@/components/protected-route";
import { AdminRoute } from "@/components/admin-route";
import { EditorRoute } from "@/components/editor-route";

// Eager (small + always-needed by trainees)
import TrainPage from "./train";

const RetrainSkippedPage = lazy(() => import("./retrain-skipped"));

// Lazy — special, reviews, admin, editor split off the trainee main bundle
const SpecialPage = lazy(() => import("./special/index"));
const SpecialHistoryPage = lazy(() => import("./special/history"));
const SpecialClientPage = lazy(() => import("./special/scenario"));

const ReviewsDashboardPage = lazy(() => import("./reviews/index"));
const BestResponsesPage = lazy(() => import("./reviews/best"));
const BestPickHistoryPage = lazy(() => import("./reviews/pick-history"));
const TraineeSessionListPage = lazy(() => import("./reviews/user"));
const ComparePage = lazy(() => import("./reviews/compare"));
const SessionDetailPage = lazy(() => import("./reviews/session"));
const PickBestPage = lazy(() => import("./reviews/pick"));

const AdminPage = lazy(() => import("./admin/index"));
const AdminSpecialPage = lazy(() => import("./admin/special/index"));
const PendingScenariosPage = lazy(() => import("./admin/special/pending/list"));
const PendingScenarioDetailPage = lazy(() => import("./admin/special/pending/edit"));
const UploadSpecialScenarioPage = lazy(() => import("./admin/special/upload"));
const ImportsListPage = lazy(() => import("./admin/imports/list"));
const ImportReviewPage = lazy(() => import("./admin/imports/review"));

const EditorPage = lazy(() => import("./editor/index"));
const CategoriesPage = lazy(() => import("./editor/categories"));
const ConversationsListPage = lazy(() => import("./editor/conversations/list"));
const ConversationDetailPage = lazy(() => import("./editor/conversations/edit"));
const NewConversationPage = lazy(() => import("./editor/conversations/new"));

function lazyEl(node: ReactNode) {
  return <Suspense fallback={<Loading />}>{node}</Suspense>;
}

function protect(element: ReactNode) {
  return <ProtectedRoute>{element}</ProtectedRoute>;
}

function admin(element: ReactNode) {
  return <AdminRoute>{element}</AdminRoute>;
}

function editor(element: ReactNode) {
  return <EditorRoute>{element}</EditorRoute>;
}

export const router = createBrowserRouter([
  { path: "/login", element: <LoginPage /> },

  { path: "/", element: protect(<Home />) },
  { path: "/train", element: protect(<TrainPage />) },
  { path: "/train/retrain-skipped", element: protect(lazyEl(<RetrainSkippedPage />)) },

  { path: "/special", element: protect(lazyEl(<SpecialPage />)) },
  { path: "/special/history", element: protect(lazyEl(<SpecialHistoryPage />)) },
  { path: "/special/:clientName", element: protect(lazyEl(<SpecialClientPage />)) },

  { path: "/reviews", element: protect(lazyEl(<ReviewsDashboardPage />)) },
  { path: "/reviews/best", element: protect(lazyEl(<BestResponsesPage />)) },
  { path: "/reviews/pick-history", element: admin(lazyEl(<BestPickHistoryPage />)) },
  { path: "/reviews/pick", element: admin(lazyEl(<PickBestPage />)) },
  { path: "/reviews/compare/:conversationId", element: admin(lazyEl(<ComparePage />)) },
  { path: "/reviews/sessions/:sessionId", element: protect(lazyEl(<SessionDetailPage />)) },
  { path: "/reviews/:userId", element: protect(lazyEl(<TraineeSessionListPage />)) },

  { path: "/admin", element: admin(lazyEl(<AdminPage />)) },
  { path: "/admin/imports", element: admin(lazyEl(<ImportsListPage />)) },
  { path: "/admin/imports/:id", element: admin(lazyEl(<ImportReviewPage />)) },
  { path: "/admin/special", element: admin(lazyEl(<AdminSpecialPage />)) },
  { path: "/admin/special/pending", element: admin(lazyEl(<PendingScenariosPage />)) },
  { path: "/admin/special/pending/:id", element: admin(lazyEl(<PendingScenarioDetailPage />)) },
  { path: "/admin/special/upload", element: admin(lazyEl(<UploadSpecialScenarioPage />)) },

  { path: "/editor", element: editor(lazyEl(<EditorPage />)) },
  { path: "/editor/categories", element: editor(lazyEl(<CategoriesPage />)) },
  { path: "/editor/conversations", element: editor(lazyEl(<ConversationsListPage />)) },
  { path: "/editor/conversations/new", element: editor(lazyEl(<NewConversationPage />)) },
  { path: "/editor/conversations/:id", element: editor(lazyEl(<ConversationDetailPage />)) },

  { path: "*", element: <NotFound /> },
  { path: "/index.html", element: <Navigate to="/" replace /> },
]);
